import React, { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, Send, User, Volume2, VolumeX, X } from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import { useLocation } from "react-router-dom";

const API_BASE = (
  import.meta.env.VITE_BACKEND_API_BASE_URL ||
  import.meta.env.VITE_AUTH_API_BASE_URL ||
  "/api"
).replace(/\/+$/, "");
const SUPPORT_WIDGET_ENABLED = import.meta.env.VITE_SUPPORT_CHAT_ENABLED !== "false";

const GUEST_STORAGE_KEY = "gainwise_support_guest_v1";
const SESSION_STORAGE_KEY = "gainwise_support_session_v1";
const SOUND_PREF_KEY = "gainwise_support_sound_v1";
const POLL_INTERVAL_MS = 8000;

const routeLabels = {
  "/": "Home",
  "/about": "About",
  "/contact": "Contact",
  "/login": "Login",
  "/signup": "Sign Up",
  "/dashboard": "Dashboard",
  "/trade": "Markets",
  "/trade-live": "Trade Live",
  "/wallet": "Wallet",
  "/profile": "Profile",
  "/settings": "Settings",
  "/payment": "Payment",
  "/payment-callback": "Payment Callback",
};

const getStoredGuestProfile = () => {
  if (typeof window === "undefined") {
    return { guestKey: "", guestName: "", guestEmail: "" };
  }

  try {
    const parsed = JSON.parse(window.localStorage.getItem(GUEST_STORAGE_KEY) || "{}");
    return {
      guestKey: parsed.guestKey || "",
      guestName: parsed.guestName || "",
      guestEmail: parsed.guestEmail || "",
    };
  } catch {
    return { guestKey: "", guestName: "", guestEmail: "" };
  }
};

const getOrCreateSessionId = () => {
  if (typeof window === "undefined") {
    return "server";
  }

  const existing = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
  if (existing) {
    return existing;
  }

  const created = `support-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
  window.sessionStorage.setItem(SESSION_STORAGE_KEY, created);
  return created;
};

const detectDeviceType = () => {
  if (typeof window === "undefined") {
    return "desktop";
  }

  const width = window.innerWidth;
  if (width < 768) {
    return "mobile";
  }
  if (width < 1100) {
    return "tablet";
  }
  return "desktop";
};

const formatMessageTime = (value) => {
  if (!value) {
    return "";
  }

  try {
    return new Date(value).toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
};

const getRouteLabel = (pathname) => routeLabels[pathname] || pathname.replace(/^\//, "").replace(/-/g, " ") || "Home";

const createHeaders = (token) => {
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
    headers["X-Auth-Token"] = token;
  }

  return headers;
};

const SupportChatWidget = () => {
  if (!SUPPORT_WIDGET_ENABLED) {
    return null;
  }

  const { user, token, isAuthenticated } = useAuth();
  const location = useLocation();
  const initialGuestProfile = useMemo(() => getStoredGuestProfile(), []);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState("");
  const [messages, setMessages] = useState([]);
  const [conversation, setConversation] = useState(null);
  const [messageDraft, setMessageDraft] = useState("");
  const [guestName, setGuestName] = useState(initialGuestProfile.guestName);
  const [guestEmail, setGuestEmail] = useState(initialGuestProfile.guestEmail);
  const [guestKey, setGuestKey] = useState(initialGuestProfile.guestKey);
  const [soundEnabled, setSoundEnabled] = useState(
    typeof window === "undefined" ? true : window.localStorage.getItem(SOUND_PREF_KEY) !== "off"
  );
  const [hasInteracted, setHasInteracted] = useState(false);
  const [sequenceNumber, setSequenceNumber] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [lastPath, setLastPath] = useState(location.pathname);
  const [lastVisitedAt, setLastVisitedAt] = useState(Date.now());
  const conversationEndRef = useRef(null);
  const inputRef = useRef(null);
  const pollRef = useRef(null);
  const lastMessageIdRef = useRef(0);
  const sessionIdRef = useRef(getOrCreateSessionId());
  const lastVisitSignatureRef = useRef("");

  const isMember = isAuthenticated && !!user;
  const displayName = user?.name || user?.firstName || guestName || "Guest";

  const persistGuestProfile = (nextGuestKey = guestKey, nextGuestName = guestName, nextGuestEmail = guestEmail) => {
    if (typeof window === "undefined") {
      return;
    }

    const payload = {
      guestKey: nextGuestKey || "",
      guestName: nextGuestName || "",
      guestEmail: nextGuestEmail || "",
    };
    window.localStorage.setItem(GUEST_STORAGE_KEY, JSON.stringify(payload));
  };

  const playSound = () => {
    if (!soundEnabled || !hasInteracted || typeof window === "undefined") {
      return;
    }

    try {
      const AudioContextRef = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextRef) {
        return;
      }
      const audioContext = new AudioContextRef();
      const oscillator = audioContext.createOscillator();
      const gain = audioContext.createGain();

      oscillator.type = "sine";
      oscillator.frequency.setValueAtTime(880, audioContext.currentTime);
      gain.gain.setValueAtTime(0.001, audioContext.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.12, audioContext.currentTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.3);

      oscillator.connect(gain);
      gain.connect(audioContext.destination);
      oscillator.start();
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch {
      // Ignore sound failures in browsers that block autoplay until interaction.
    }
  };

  const syncConversationState = (payload, options = {}) => {
    const nextConversation = payload?.conversation || null;
    const nextMessages = payload?.messages || [];

    setConversation(nextConversation);
    setMessages(nextMessages);
    setUnreadCount(nextConversation?.unreadForUser || 0);

    if (nextMessages.length) {
      const latestId = nextMessages[nextMessages.length - 1]?.id || 0;
      if (
        options.playAlert &&
        latestId > lastMessageIdRef.current &&
        nextMessages[nextMessages.length - 1]?.senderType === "admin"
      ) {
        playSound();
      }
      lastMessageIdRef.current = latestId;
    }

    if (payload?.guestKey) {
      setGuestKey(payload.guestKey);
      persistGuestProfile(payload.guestKey, guestName, guestEmail);
    }
  };

  const loadConversation = async ({ silent = false, playAlert = false } = {}) => {
    if (!silent) {
      setIsLoading(true);
    }
    setError("");

    const params = new URLSearchParams();
    if (!isMember && guestKey) {
      params.set("guestKey", guestKey);
    }
    if (!isMember && guestEmail) {
      params.set("guestEmail", guestEmail);
    }

    try {
      const response = await fetch(`${API_BASE}/support-thread.php${params.toString() ? `?${params}` : ""}`, {
        headers: createHeaders(token),
      });
      const payload = await response.json();
      if (!response.ok || payload?.success === false) {
        throw new Error(payload?.message || "Unable to load support chat.");
      }

      syncConversationState(payload, { playAlert });
    } catch (requestError) {
      if (!silent) {
        setError(requestError.message || "Unable to load support chat.");
      }
    } finally {
      if (!silent) {
        setIsLoading(false);
      }
    }
  };

  const logVisit = async ({ pathname, referrerPath, durationMs, sourceType }) => {
    const identitySignature = isMember
      ? `member:${user?.id || ""}:${user?.email || ""}`
      : `guest:${guestKey || ""}:${guestEmail || ""}:${guestName || ""}`;
    const visitSignature = [
      pathname,
      referrerPath || "",
      sourceType || "",
      identitySignature,
    ].join("|");

    if (lastVisitSignatureRef.current === visitSignature) {
      return;
    }

    try {
      await fetch(`${API_BASE}/support-log-visit.php`, {
        method: "POST",
        headers: createHeaders(token),
        body: JSON.stringify({
          guestKey: isMember ? "" : guestKey,
          guestEmail: isMember ? (user?.email || "") : guestEmail,
          userEmail: isMember ? (user?.email || "") : "",
          path: pathname,
          pageLabel: getRouteLabel(pathname),
          referrerPath,
          sourceType,
          deviceType: detectDeviceType(),
          sessionId: sessionIdRef.current,
          sequenceNumber: sequenceNumber + 1,
          durationMs,
        }),
      });
      lastVisitSignatureRef.current = visitSignature;
      setSequenceNumber((current) => current + 1);
    } catch {
      // Visit logging is non-blocking.
    }
  };

  useEffect(() => {
    loadConversation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMember, token]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const markInteracted = () => setHasInteracted(true);
    window.addEventListener("pointerdown", markInteracted, { once: true });
    return () => window.removeEventListener("pointerdown", markInteracted);
  }, []);

  useEffect(() => {
    if (!guestKey && !guestEmail && !guestName) {
      return;
    }
    persistGuestProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guestKey, guestEmail, guestName]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(SOUND_PREF_KEY, soundEnabled ? "on" : "off");
    }
  }, [soundEnabled]);

  useEffect(() => {
    if (!conversationEndRef.current) {
      return;
    }
    conversationEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, isOpen]);

  useEffect(() => {
    if (isMember) {
      setLastPath(location.pathname);
      setLastVisitedAt(Date.now());
      return;
    }

    const duration = Date.now() - lastVisitedAt;
    logVisit({
      pathname: location.pathname,
      referrerPath: lastPath,
      durationMs: duration,
      sourceType: lastPath === location.pathname ? "direct" : "internal",
    });
    setLastPath(location.pathname);
    setLastVisitedAt(Date.now());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    if (isMember) {
      return;
    }

    const hasIdentity = isMember
      ? Boolean(user?.email || user?.id)
      : Boolean(guestEmail || guestKey || guestName);

    if (!hasIdentity) {
      return;
    }

    logVisit({
      pathname: location.pathname,
      referrerPath: lastPath,
      durationMs: Math.max(0, Date.now() - lastVisitedAt),
      sourceType: "active-session",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMember, user?.id, user?.email, guestEmail, guestKey, guestName]);

  useEffect(() => {
    if (pollRef.current) {
      window.clearInterval(pollRef.current);
    }

    pollRef.current = window.setInterval(() => {
      loadConversation({ silent: true, playAlert: true });
    }, POLL_INTERVAL_MS);

    return () => {
      if (pollRef.current) {
        window.clearInterval(pollRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, guestKey, guestEmail, isMember]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    setHasInteracted(true);
    setError("");

    if (!messageDraft.trim()) {
      setError("Message cannot be empty.");
      return;
    }

    if (!isMember) {
      if (!guestName.trim() || !guestEmail.trim()) {
        setError("Please enter your name and email first.");
        return;
      }
    }

    setIsSending(true);

    try {
      const response = await fetch(`${API_BASE}/support-send-message.php`, {
        method: "POST",
        headers: createHeaders(token),
        body: JSON.stringify({
          message: messageDraft,
          guestKey,
          guestName,
          guestEmail,
          currentPath: location.pathname,
          pageLabel: getRouteLabel(location.pathname),
          referrerPath: lastPath,
          sourceType: "chat",
          deviceType: detectDeviceType(),
          sessionId: sessionIdRef.current,
          sequenceNumber,
          durationMs: Date.now() - lastVisitedAt,
        }),
      });

      const payload = await response.json();
      if (!response.ok || payload?.success === false) {
        throw new Error(payload?.message || "Unable to send your message.");
      }

      setMessageDraft("");
      syncConversationState(payload);
    } catch (requestError) {
      setError(requestError.message || "Unable to send your message.");
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSubmit();
    }
  };

  return (
    <>
      <div className="fixed bottom-4 right-4 z-[120] sm:bottom-6 sm:right-6">
        <AnimatePresence>
          {!isOpen && unreadCount > 0 ? (
            <motion.div
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.6, opacity: 0 }}
              className="absolute -top-2 right-0 flex h-6 min-w-[1.5rem] items-center justify-center rounded-full border border-white/25 bg-rose-500 px-2 text-xs font-semibold text-white shadow-lg"
            >
              {Math.min(unreadCount, 9)}+
            </motion.div>
          ) : null}
        </AnimatePresence>

        <button
          type="button"
          onClick={() => setIsOpen((current) => !current)}
          className="group flex h-16 w-16 items-center justify-center rounded-[1.7rem] border border-cyan-300/40 bg-gradient-to-br from-sky-400 via-cyan-400 to-blue-500 text-white shadow-[0_18px_45px_rgba(14,165,233,0.34)] transition duration-300 hover:-translate-y-1"
          aria-label="Open support chat"
        >
          <MessageCircle className="h-7 w-7 transition-transform duration-300 group-hover:scale-110" />
        </button>
      </div>

      <AnimatePresence>
        {isOpen ? (
          <motion.div
            initial={{ opacity: 0, y: 32, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-24 right-4 z-[121] w-[calc(100vw-2rem)] max-w-[24rem] overflow-hidden rounded-[2rem] border border-slate-700/80 bg-[#07101f]/95 shadow-[0_36px_90px_rgba(2,6,23,0.52)] backdrop-blur-xl sm:bottom-28 sm:right-6"
          >
            <div className="flex items-start justify-between border-b border-slate-800 px-5 py-5">
              <div>
                <div className="text-xl font-semibold text-white">Support</div>
                <div className="mt-1 text-sm text-slate-400">
                  Replies update without refreshing
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setSoundEnabled((current) => !current)}
                  className="rounded-full border border-slate-700 bg-slate-900/70 p-2 text-slate-300 transition hover:border-slate-500 hover:text-white"
                  aria-label={soundEnabled ? "Mute support notifications" : "Enable support notifications"}
                >
                  {soundEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
                </button>
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="rounded-full border border-slate-700 bg-slate-900/70 p-3 text-slate-300 transition hover:border-slate-500 hover:text-white"
                  aria-label="Close support chat"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {!isMember ? (
              <div className="border-b border-slate-800 px-5 py-4">
                <div className="mb-3 flex items-center gap-2 text-sm font-medium text-slate-300">
                  <User className="h-4 w-4 text-cyan-300" />
                  Guest details
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <input
                    type="text"
                    value={guestName}
                    onChange={(event) => setGuestName(event.target.value)}
                    placeholder="Your name"
                    className="w-full rounded-2xl border border-slate-700 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition placeholder:text-slate-500 focus:border-cyan-400"
                  />
                  <input
                    type="email"
                    value={guestEmail}
                    onChange={(event) => setGuestEmail(event.target.value)}
                    placeholder="Your email"
                    className="w-full rounded-2xl border border-slate-700 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition placeholder:text-slate-500 focus:border-cyan-400"
                  />
                </div>
              </div>
            ) : (
              <div className="border-b border-slate-800 px-5 py-3 text-sm text-slate-400">
                Signed in as <span className="font-medium text-white">{displayName}</span>
              </div>
            )}

            <div className="max-h-[20rem] min-h-[16rem] overflow-y-auto px-5 py-4">
              {isLoading ? (
                <div className="flex h-40 items-center justify-center text-sm text-slate-400">
                  Loading support chat...
                </div>
              ) : messages.length ? (
                <div className="space-y-3">
                  {messages.map((message) => {
                    const ownMessage = message.senderType !== "admin";
                    return (
                      <div
                        key={message.id}
                        className={`flex ${ownMessage ? "justify-end" : "justify-start"}`}
                      >
                        <div
                          className={`max-w-[82%] rounded-[1.4rem] px-4 py-3 text-sm shadow-lg ${
                            ownMessage
                              ? "bg-gradient-to-br from-sky-400 via-cyan-400 to-blue-500 text-white"
                              : "border border-slate-700 bg-slate-900/80 text-slate-100"
                          }`}
                        >
                          <div className="whitespace-pre-wrap break-words">{message.body}</div>
                          <div
                            className={`mt-2 text-[11px] ${
                              ownMessage ? "text-white/75" : "text-slate-400"
                            }`}
                          >
                            {formatMessageTime(message.createdAt)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={conversationEndRef} />
                </div>
              ) : (
                <div className="flex h-48 flex-col items-center justify-center text-center text-slate-400">
                  <div className="mb-4 rounded-full border border-slate-700 p-3 text-slate-200">
                    <MessageCircle className="h-6 w-6" />
                  </div>
                  <div className="max-w-[15rem] text-base">
                    Start a conversation and it will appear here for future visits.
                  </div>
                </div>
              )}
            </div>

            <div className="border-t border-slate-800 px-5 py-4">
              <div className="flex items-end gap-3">
                <textarea
                  ref={inputRef}
                  rows={2}
                  value={messageDraft}
                  onChange={(event) => setMessageDraft(event.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Write your message"
                  className="min-h-[5.4rem] flex-1 resize-none rounded-[1.5rem] border border-slate-700 bg-slate-950/80 px-4 py-4 text-base text-white outline-none transition placeholder:text-slate-500 focus:border-cyan-400"
                />
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSending}
                  className="flex h-14 min-w-[9.4rem] items-center justify-center gap-2 rounded-[1.5rem] bg-gradient-to-r from-sky-400 to-blue-500 px-5 text-base font-semibold text-white shadow-lg transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  <Send className="h-4 w-4" />
                  {isSending ? "Sending..." : "Send"}
                </button>
              </div>
              {error ? <div className="mt-3 text-sm font-medium text-rose-300">{error}</div> : null}
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </>
  );
};

export default SupportChatWidget;
